<div class="flex items-start gap-[12px] w-full">
  @let view = notice() | noticeContent;

  <!-- 左侧统一系统消息图标：白底灰边圆 + icon-xitongxiaoxi（与左栏系统消息入口一致） -->
  <div
    class="shrink-0 size-[32px] rounded-full flex items-center justify-center bg-white"
  >
    <lib-fl-icon name="icon-xitongxiaoxi" size="32"></lib-fl-icon>
  </div>

  <!-- 右侧气泡卡片 -->
  <div class="flex-1 min-w-0 rounded-[6px] border-[0.5px] border-neutral-n5 overflow-hidden justify-end">
    <!-- 标题栏：结构化消息由前端按【事务+状态】补前缀；旧消息保留【标签】前缀 -->
    <div
      class="px-[16px] py-[12px] text-[16px] break-all"
      [class.bg-primary-b7]="view?.status !== NoticeStatus.Failure"
      [class.text-primary]="view?.status !== NoticeStatus.Failure"
      [class.bg-red-r6]="view?.status === NoticeStatus.Failure"
      [class.text-red-r1]="view?.status === NoticeStatus.Failure"
    >
      @if (!view && tagText) {
        <span class="font-medium">{{ tagText }}</span>
      }
      <span>{{ view ? view.title : notice().title }}</span>
    </div>

    <!-- 内容区：白色背景 -->
    <div class="p-[16px] bg-white space-y-[8px]">
      @if (view) {
        <!-- 内容行：成功时是范围/数量等正文，失败时已包含引导语 -->
        @if (view.detail) {
          <p class="text-[13px] text-neutral-n2 whitespace-pre-line break-all">{{ view.detail }}</p>
        }

        <!-- 成功态引导行；失败引导语已在 detail 中 -->
        @if (view.guidance) {
          <p class="text-[13px] text-neutral-n3">{{ view.guidance }}</p>
        }

        <!-- 错误详情：文档口径只展示错误摘要，完整 traceback 仅进入复制内容 -->
        @if (view.errorMessage) {
          <p class="text-[12px] text-neutral-n3 font-mono break-all">{{ view.errorMessage }}</p>
        }

        <!-- 转码产物卡片：按产品文档放在内容行/引导行之后 -->
        @if (view.items.length) {
          <div class="space-y-[8px]">
            @for (item of view.items; track $index) {
              @let meta = item | noticeItemMeta;
              <div
                class="flex items-center gap-[8px] rounded-[4px] border border-neutral-n5 px-[12px] py-[8px]"
                [class.cursor-pointer]="!!item.url"
                [class.opacity-50]="!item.url"
                (click)="onOpenItem(item)"
              >
                <img [src]="meta.iconPath" alt="file icon" class="w-6 h-6 shrink-0" />
                <span class="flex-1 min-w-0 truncate text-[13px] text-neutral-n1">{{ item.name }}</span>
                @if (meta.isVideo) {
                  @if (meta.metaText) {
                    <span class="shrink-0 text-[12px] text-neutral-n3">{{ meta.metaText }}</span>
                  }
                } @else if (item.size) {
                  <span class="shrink-0 text-[12px] text-neutral-n3">{{ item.size | fmtFileSize }}</span>
                }
                @if (item.url) {
                  <i nz-icon nzType="play-circle" class="shrink-0 text-[16px] text-neutral-n3"></i>
                }
              </div>
            }
          </div>
        }

        <!-- 操作按钮：下载 / 复制（重试本期不做） -->
        @if (view.canDownload || view.canCopy) {
          <div class="flex items-center gap-[8px]">
            @if (view.canDownload) {
              <button type="button" nz-button nzType="primary" nzGhost (click)="onDownload(view.url)">下载</button>
            }
            @if (view.canCopy) {
              <button type="button" nz-button nzType="primary" nzGhost (click)="onCopyError(view)">复制</button>
            }
          </div>
        }
      } @else {
        <!-- 反馈类：学生名 + 反馈文本 + 图片缩略图 -->
        @if (discussContent) {
          <p class="text-[13px] text-neutral-n2 whitespace-pre-line">
            @if (discussContent.firstName) {
              <span class="font-medium text-neutral-n1">{{ discussContent.firstName }}：</span>
            }
            {{ discussContent.content }}
          </p>
          @if (discussContent.photos?.length) {
            <div class="flex flex-wrap gap-[8px]">
              @for (photo of discussContent.photos; track photo) {
                <img [nzSrc]="photo" nz-image class="max-w-[90px] max-h-[90px] rounded-[6px] object-cover" />
              }
            </div>
          }
        }

        <!-- 导出/下载类：content 为 safe HTML 下载链接 -->
        @if (htmlContent) {
          <div class="text-[13px]" [innerHTML]="htmlContent | safe: 'html'"></div>
        }

        <!-- 视频转码类：文件名 + cover 缩略图（cover 为 null 退化为文件名链接） -->
        @if (videoList.length) {
          <div class="space-y-[6px]">
            @for (video of videoList; track video.url) {
              <div class="flex items-center gap-[8px] cursor-pointer text-primary" (click)="playVideo(video)">
                @if (video.cover) {
                  <img [src]="video.cover" class="shrink-0 w-[90px] h-[60px] rounded-[6px] object-cover bg-neutral-n5" />
                }
                <span class="min-w-0 truncate underline text-[13px]">{{ video.name }}</span>
              </div>
            }
          </div>
        }

        <!-- 导入报告类：success/error 分组展示（成功中性色 / 失败红色），并标注成功/失败状态 -->
        @if (importSuccess.length || importError.length) {
          <div class="space-y-[6px]">
            <div class="text-[13px]">成功:</div>
            @for (entry of importSuccess; track entry.name) {
              <div class="text-[13px] pl-[12px]">
                <div class="break-all">{{ entry.name }}</div>
                <div>{{ entry.message }}</div>
              </div>
            }
            <div class="text-[13px]">失败:</div>
            @for (entry of importError; track entry.name) {
              <div class="text-[13px] pl-[12px]">
                <div class="break-all">{{ entry.name }}</div>
                <div>{{ entry.message }}</div>
              </div>
            }
          </div>
        }

        <!-- 其他：content 整块文本（保留换行） -->
        @if (textContent && !discussContent && !videoList.length && !htmlContent) {
          <p class="text-[13px] text-neutral-n2 whitespace-pre-line">{{ textContent }}</p>
        }

        <!-- 操作按钮：仅反馈类有回复 -->
        @if (showReply) {
          <button
            type="button"
            nz-button
            nzType="primary"
            nzSize="small"
            class="block"
            (click)="onReply()"
          >
            回复
          </button>
        }
      }
    </div>
  </div>
</div>
